// src/components/Integrations/IntegrationsGallery.jsx

import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import InsightsIcon from '@mui/icons-material/Insights';
import StorageIcon from '@mui/icons-material/Storage';
import { useOrganization2 } from '../../contexts/OrganizationContext2';
import {
  AVAILABLE_INTEGRATIONS,
  INTEGRATION_IDS,
  getStatusLabel,
  getStatusSeverity,
} from './integrationsRegistry';
import {
  fetchIntegrationStatus,
} from './integrationFunctions';

function getIntegrationIcon(integrationId) {
  if (integrationId === INTEGRATION_IDS.BITLY) {
    return <LinkIcon fontSize="small" />;
  }

  if (integrationId === INTEGRATION_IDS.BIGQUERY) {
    return <StorageIcon fontSize="small" />;
  }

  if (integrationId === INTEGRATION_IDS.LOOKER) {
    return <InsightsIcon fontSize="small" />;
  }

  return <LinkIcon fontSize="small" />;
}

export default function IntegrationsGallery({
  onSelectIntegration,
  refreshKey = 0,
}) {
  const { organization } = useOrganization2();
  const [statuses, setStatuses] = useState({});
  const [checkingStatus, setCheckingStatus] = useState(true);

  const integrations = useMemo(() => AVAILABLE_INTEGRATIONS, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchStatuses() {
      if (!organization?.id) {
        setStatuses({});
        setCheckingStatus(false);
        return;
      }

      try {
        setCheckingStatus(true);

        const entries = await Promise.all(
          integrations.map(async (integration) => {
            const status = await fetchIntegrationStatus({
              organizationId: organization.id,
              provider: integration.id,
            });

            return [
              integration.id,
              status?.status === 'not_installed' ? '' : status?.status || '',
            ];
          })
        );

        if (!cancelled) {
          setStatuses(Object.fromEntries(entries));
        }
      } catch (err) {
        console.error(
          '[IntegrationsGallery] Failed to fetch statuses:',
          err
        );

        if (!cancelled) {
          setStatuses({});
        }
      } finally {
        if (!cancelled) {
          setCheckingStatus(false);
        }
      }
    }

    fetchStatuses();

    return () => {
      cancelled = true;
    };
  }, [organization?.id, integrations, refreshKey]);

  return (
    <Box>
      <Stack spacing={0.75} sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Integrations
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Connect organization-level tools and choose where each integration
          is used across your forms.
        </Typography>
      </Stack>

      <Grid container spacing={2.5}>
        {integrations.map((integration) => {
          const status = statuses[integration.id] || '';
          const severity = getStatusSeverity(status);

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={integration.id}
            >
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  transition: 'box-shadow 160ms ease',
                  '&:hover': {
                    boxShadow: 2,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => onSelectIntegration?.(integration)}
                  sx={{
                    height: '100%',
                    alignItems: 'stretch',
                  }}
                >
                  <CardContent sx={{ height: '100%' }}>
                    <Stack spacing={2} sx={{ height: '100%' }}>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: 'primary.main',
                          }}
                        >
                          {getIntegrationIcon(integration.id)}
                        </Avatar>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            noWrap
                          >
                            {integration.name}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {integration.category}
                          </Typography>
                        </Box>
                      </Stack>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ flex: 1 }}
                      >
                        {integration.description}
                      </Typography>

                      <Stack spacing={1}>
                        {checkingStatus ? (
                          <Skeleton width={116} height={28} />
                        ) : (
                          <Chip
                            size="small"
                            label={getStatusLabel(status)}
                            color={severity}
                            variant={status ? 'filled' : 'outlined'}
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {integration.helperText}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
